import { isBefore } from "date-fns";
import { saveAs } from "file-saver";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Icon } from "semantic-ui-react";

import { Can, DATE_FORMATS_FNS } from "components";
import ENV from "config/Env";
import { ACL_ACTIVITIES, ACL_RESOURCES, COMMUNICATION_STATUSES, STATUS_IDENTIFIERS } from "models";
import { CUSTOMER_COMMUNICATIONS, CustomerCommunicationModal } from "modules/AppointmentDetails/components";
import "modules/AppointmentDetails/components/CustomerCommunicationButtons/CustomerCommunicationButtons.scss";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { ApiInstance } from "utils/Api";
import { isCarReadyOrQualityCheck, isWorkNotStarted } from "utils/appointmentUtils";
import { formatDate } from "utils/dateHelperFunctions";

import type { Appointment, DealerLocation } from "models";

type CustomerCommunicationButtonsProps = {
  appointment: Appointment;
};

const handleCanSendDiagnoseOverview = (appointmentStatus: STATUS_IDENTIFIERS, location?: DealerLocation, communicationStatus?: COMMUNICATION_STATUSES | null) => {
  return (
    location?.diagnose_overview_enabled &&
    (!communicationStatus || communicationStatus < COMMUNICATION_STATUSES.DiagnoseOverviewSent) &&
    !isCarReadyOrQualityCheck(appointmentStatus) &&
    !isWorkNotStarted(appointmentStatus) &&
    ![STATUS_IDENTIFIERS.CustomerOKStatus, STATUS_IDENTIFIERS.CarCheckStartedStatus].includes(appointmentStatus)
  );
};

const handleCanSendRepairOverview = (appointmentStatus: STATUS_IDENTIFIERS, location?: DealerLocation, communicationStatus?: COMMUNICATION_STATUSES | null) => {
  return (
    location?.repair_overview_enabled &&
    (!communicationStatus || communicationStatus < COMMUNICATION_STATUSES.RepairOverviewSent) &&
    isCarReadyOrQualityCheck(appointmentStatus)
  );
};

const handleCanSendOnlineCheckin = (
  appointmentStatus: STATUS_IDENTIFIERS,
  appointmentDate: string,
  location?: DealerLocation,
  communicationStatus?: COMMUNICATION_STATUSES | null
) => {
  return (
    location?.online_check_in_enabled &&
    !communicationStatus &&
    [STATUS_IDENTIFIERS.NewCarStatus, STATUS_IDENTIFIERS.CarInShop].includes(appointmentStatus) &&
    isBefore(formatDate(new Date(), DATE_FORMATS_FNS.monthDayYear), formatDate(appointmentDate, DATE_FORMATS_FNS.monthDayYear)) &&
    !isCarReadyOrQualityCheck(appointmentStatus)
  );
};

const getCommunicationIcon = (appointmentStatus: STATUS_IDENTIFIERS, communicationStatus?: COMMUNICATION_STATUSES | null) => {
  switch (communicationStatus) {
    case COMMUNICATION_STATUSES.DiagnoseOverviewSent:
      return <Icon className="eye slash" />;
    case COMMUNICATION_STATUSES.DiagnoseOverviewOpened:
      return <Icon className="eye" />;
    case COMMUNICATION_STATUSES.DiagnoseOverviewAnswered:
      return <Icon className="user check" />;
    case COMMUNICATION_STATUSES.DiagnoseOverviewClosed:
      return !isCarReadyOrQualityCheck(appointmentStatus) ? <Icon className="lock" /> : null;
    case COMMUNICATION_STATUSES.RepairOverviewSent:
    case COMMUNICATION_STATUSES.RepairOverviewOpened:
      return <Icon className="file invoice dollar" />;
    default:
      return null;
  }
};

const getRepairOverviewPDF = async (receptionistKey: string) => {
  const config = { responseType: "blob" };
  const payload = { communication_key: receptionistKey };
  return ApiInstance.post("/repair_overview/download", payload, ENV.customComBaseURL, config);
};

export const CustomerCommunicationButtons = ({ appointment }: CustomerCommunicationButtonsProps) => {
  if (appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CanceledStatus) return null;

  const { t } = useTranslation();
  const selectedLocation = useAppointmentLocation();
  const [showModal, setShowModal] = useState<boolean>(false);
  const receptionistKey = appointment.customer_communication?.receptionist_key;
  const canSendDiagnoseOverview = handleCanSendDiagnoseOverview(appointment.appointment_status_identifier, selectedLocation, appointment.customer_communication?.status);
  const canSendRepairOverview = handleCanSendRepairOverview(appointment.appointment_status_identifier, selectedLocation, appointment.customer_communication?.status);
  const canSendOnlineCheckin = handleCanSendOnlineCheckin(
    appointment.appointment_status_identifier,
    appointment.time_car_app,
    selectedLocation,
    appointment.customer_communication?.status
  );

  const onDownloadRepairOverviewPdf = () => {
    if (receptionistKey) {
      getRepairOverviewPDF(receptionistKey)
        .then(result => {
          saveAs(result, `RepairOverview_${appointment.reg_number_escaped}_WO_${appointment.wo_nr}_${formatDate(new Date(), DATE_FORMATS_FNS.yearMonthDate)}.pdf`);
        })
        .catch(error => {
          console.error("Error downloading repair overview report.", error);
          toast.error(t("v8_something_went_wrong", "Something went wrong, please contact the administrator."));
        });
    }
  };

  const handleOpenCC = (key: string) => {
    const url = selectedLocation?.is_service_tracker_enabled ? `${ENV.commBaseURL}/service-tracker/${key}` : `${ENV.commBaseURL}/#${key}`;
    window.open(url, "_blank");
  };

  const getSendCommunication = () => {
    if (canSendOnlineCheckin) return CUSTOMER_COMMUNICATIONS.OnlineCheckin;

    if (canSendDiagnoseOverview) return CUSTOMER_COMMUNICATIONS.DiagnoseOverview;

    return CUSTOMER_COMMUNICATIONS.RepairOverview;
  };

  return (
    <>
      {appointment.customer_communication?.status && appointment.customer_communication.status >= COMMUNICATION_STATUSES.RepairOverviewSent && (
        <Button className="-appointment-status download-pdf" size="small" onClick={onDownloadRepairOverviewPdf}>
          <Icon name="file pdf" />
        </Button>
      )}

      {[STATUS_IDENTIFIERS.NewCarStatus, STATUS_IDENTIFIERS.CarInShop].includes(appointment.appointment_status_identifier) && (
        <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.OnlineCheckIn}>
          <Button.Group basic>
            <Button className={"-appointment-status customer-communication-icon"} size="small" disabled={!canSendOnlineCheckin} onClick={() => setShowModal(true)}>
              {getCommunicationIcon(appointment.appointment_status_identifier, appointment.customer_communication?.status)}

              <Icon className="envelope" />
            </Button>

            {receptionistKey && (
              <Button className="-appointment-status external-link-btn" size="small" onClick={() => handleOpenCC(receptionistKey)}>
                <Icon className="external link alt" />
              </Button>
            )}
          </Button.Group>
        </Can>
      )}

      {![STATUS_IDENTIFIERS.NewCarStatus, STATUS_IDENTIFIERS.CarInShop].includes(appointment.appointment_status_identifier) && (
        <Can I={[ACL_ACTIVITIES.Create, ACL_ACTIVITIES.Update]} the={[ACL_RESOURCES.DiagnoseOverview, ACL_RESOURCES.CustomcomRepairOverview]}>
          <Button.Group basic>
            <Button
              className={`-appointment-status customer-communication-icon ${receptionistKey ? "border" : ""}`}
              size="small"
              disabled={!canSendDiagnoseOverview && !canSendRepairOverview}
              onClick={() => setShowModal(true)}
            >
              {getCommunicationIcon(appointment.appointment_status_identifier, appointment.customer_communication?.status)}

              <Icon className="envelope" />
            </Button>
            {receptionistKey && (
              <Button className="-appointment-status external-link-btn" size="small" onClick={() => handleOpenCC(receptionistKey)}>
                <Icon className="external link alt" />
              </Button>
            )}
          </Button.Group>
        </Can>
      )}
      {showModal && <CustomerCommunicationModal appointment={appointment} send={getSendCommunication()} onClose={() => setShowModal(false)} />}
    </>
  );
};
